/**
 * @fileoverview Status card for a legacy migration clear job (running, completed, or failed), shown inside
 *               the clear dialog and, after it is closed, on the Legacy Migration page.
 * @module system-web/components/migration/ClearJobStatusCard
 *
 * Responsibilities:
 * - Show job state (running / completed / failed) with a live elapsed timer while running.
 * - Show the per-job result after completion: documents removed, parent entities cleaned, receipts reset,
 *   runs removed, file transfers reset, and the tabs that were cleared.
 * - Show the sanitized error message when the job fails.
 *
 * Dependencies:
 * - `react` (`useEffect`, `useState`).
 * - `lucide-react` (`Loader2`, `CheckCircle2`, `XCircle`).
 * - `../../services/legacyMigrationService` (`ClearMigrationTenantSummary`).
 *
 * Architecture:
 * - Presentational. The page owns the job state; `clearMigratedData` reports progress into it.
 *   The elapsed timer is UI-only state and performs no data reads or writes.
 *
 * Notes:
 * - The server does not report per-tab progress for clear jobs, so the running state is indeterminate.
 */

import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import type { ClearMigrationProgress, ClearMigrationTenantSummary } from '../../services/legacyMigrationService';

/**
 * @contract ClearJobStatus
 * Purpose: Client-side view of one clear job, owned by `LegacyMigrationPage`.
 * Fields: status, scope, targetLabel, startedAt, finishedAt, executionMode, jobId, summaries, error.
 * Rules: `finishedAt` is set only for 'completed' or 'failed'. `summaries` is set only for 'completed'.
 * Sync: Updated from `clearMigratedData` (start, poll, and terminal states).
 */
export interface ClearJobStatus {
  status: 'running' | 'completed' | 'failed';
  scope: 'dataset' | 'department-all';
  targetLabel: string;
  datasetKey?: string;
  departmentName: string;
  startedAt: number;
  finishedAt?: number;
  executionMode?: 'inline' | 'worker';
  jobId?: string;
  progress?: ClearMigrationProgress | null;
  summaries?: ClearMigrationTenantSummary[];
  error?: string;
}

/**
 * @contract ClearJobStatusCardProps
 * Purpose: Props for `ClearJobStatusCard`.
 * Fields: job, datasetLabels, onDismiss.
 * Rules: `onDismiss` is ignored while the job is running.
 * Sync: Presentational props.
 */
export interface ClearJobStatusCardProps {
  job: ClearJobStatus;
  datasetLabels: Record<string, string>;
  onDismiss?: () => void;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function executionLabelOf(mode: ClearJobStatus['executionMode']): string {
  if (mode === 'inline') return 'System-API (localhost, inline)';
  if (mode === 'worker') return 'Worker Job (Service Bus)';
  return 'Starting…';
}

/**
 * @component ClearJobStatusCard
 * Purpose: Renders the state, timer, and result of a legacy migration clear job.
 * Props: `ClearJobStatusCardProps`.
 * Behavior: While running, shows a progress bar and the tab being removed (determinate once the server reports
 *           progress, indeterminate before that) plus a live elapsed timer. When completed, shows
 *           the removed-record totals and the tabs cleared. When failed, shows the sanitized error.
 * Data Source: props only (job state is owned by the page).
 * Constraints: No fetch, no Dexie access.
 * Performance: One 1-second interval while running; cleared on unmount or completion.
 * @reusable
 * Usage: Rendered inside `ClearMigratedDataModal` and by `LegacyMigrationPage` after the dialog closes.
 * Rules: Presentation-only.
 */
export function ClearJobStatusCard({ job, datasetLabels, onDismiss }: ClearJobStatusCardProps) {
  const isRunning = job.status === 'running';
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isRunning) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isRunning]);

  const elapsedMs = (job.finishedAt ?? now) - job.startedAt;
  const progress = job.progress ?? null;
  const percent = progress?.percent;
  const currentLabel = progress?.currentKey ? (datasetLabels[progress.currentKey] ?? progress.currentKey) : null;
  const summaries = job.summaries ?? [];
  const sum = (pick: (s: ClearMigrationTenantSummary) => number) => summaries.reduce((acc, s) => acc + pick(s), 0);
  const clearedKeys = Array.from(new Set(summaries.flatMap((s) => s.clearedDatasetKeys)));
  const clearedLabels = clearedKeys.map((key) => datasetLabels[key] ?? key);
  const removedCounts: Array<{ count: number; label: string }> = [
    { count: sum((s) => s.totalDeleted), label: 'documents' },
    { count: sum((s) => s.totalModifiedParents), label: 'parent entities cleaned' },
    { count: sum((s) => s.receiptsDeleted), label: 'grid rows reset' },
    { count: sum((s) => s.runsDeleted), label: 'run records' },
    { count: sum((s) => s.fileTransfersReset), label: 'file transfers reset' },
  ];
  const removedSummaryParts = removedCounts
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count.toLocaleString()} ${label}`);
  const nothingRemoved = removedSummaryParts.length === 0;

  const tone = job.status === 'failed'
    ? 'bg-red-50 border-red-200 text-red-900'
    : job.status === 'completed'
      ? 'bg-green-50 border-green-200 text-green-900'
      : 'bg-blue-50 border-blue-200 text-blue-900';

  return (
    <div className={`rounded-lg border p-3 text-xs space-y-2 ${tone}`} role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold">
          {job.status === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
          {job.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
          {job.status === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
          <span>
            {job.status === 'running' && `Clearing ${job.targetLabel}…`}
            {job.status === 'completed' && (job.scope === 'department-all'
              ? `All migrated data for ${job.departmentName} is removed.`
              : `All data for ${job.targetLabel} is removed.`)}
            {job.status === 'failed' && (job.scope === 'department-all'
              ? `Could not remove migrated data for ${job.departmentName}.`
              : `Could not remove data for ${job.targetLabel}.`)}
          </span>
        </div>
        <span className="font-mono text-[11px] opacity-80">
          {job.status === 'running' ? `Elapsed ${formatDuration(elapsedMs)}` : `Took ${formatDuration(elapsedMs)}`}
        </span>
      </div>

      <div className="text-[11px] opacity-80">
        {job.departmentName} · {executionLabelOf(job.executionMode)}
        {job.jobId ? ` · Job ${job.jobId.slice(-6)}` : ''}
      </div>

      {job.status === 'running' && (
        <>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
            {percent != null
              ? <div className="h-full rounded-full bg-blue-500 transition-all duration-500" style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
              : <div className="h-full w-1/3 rounded-full bg-blue-500 animate-pulse" />}
          </div>
          <p className="text-[11px] opacity-80">
            {currentLabel
              ? `Removing ${currentLabel} (${progress?.current ?? '?'}/${progress?.total ?? '?'} tabs)`
              : 'Starting… waiting for the first tab to report progress.'}
            {percent != null ? ` · ${percent}%` : ''}
          </p>
        </>
      )}

      {job.status === 'completed' && (
        <div className="space-y-1.5">
          <p className="text-[11px]">
            {nothingRemoved
              ? 'Nothing was left to delete. The data had already been removed by an earlier clear.'
              : `Removed: ${removedSummaryParts.join(' · ')}.`}
          </p>
          {clearedLabels.length > 0 && (
            <p className="text-[11px] opacity-90">
              {job.scope === 'department-all' ? 'Tabs cleared' : 'Tabs cleared (including dependent tabs)'}: {clearedLabels.join(' · ')}.
              Run these tabs again in dependency order.
            </p>
          )}
        </div>
      )}

      {job.status === 'failed' && job.error && (
        <p className="text-[11px] break-words">{job.error}</p>
      )}

      {!isRunning && onDismiss && (
        <div className="flex justify-end pt-1">
          <button type="button" onClick={onDismiss} className="text-[11px] font-semibold hover:underline">
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
